import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Target, BarChart2 } from 'lucide-react';

interface Trade {
  id: string;
  trade_date: string;
  symbol: string;
  entry_price: number;
  exit_price: number;
  position_size: number;
  trade_type: 'long' | 'short';
  result: number;
  notes: string;
  created_at: string;
  r_multiple?: number;
}

interface TradingChartsProps {
  trades: Trade[];
}

const TradingCharts: React.FC<TradingChartsProps> = ({ trades }) => {
  // Preparar datos para los gráficos
  const chartData = trades
    .sort((a, b) => new Date(a.trade_date).getTime() - new Date(b.trade_date).getTime())
    .map(trade => ({
      date: new Date(trade.trade_date).toLocaleDateString(),
      result: trade.result,
      cumulative: 0,
      type: trade.trade_type,
      r_multiple: trade.r_multiple || 0
    }));

  // Calcular resultados acumulados
  let cumulative = 0;
  chartData.forEach(data => {
    cumulative += data.result;
    data.cumulative = cumulative;
  });

  // Calcular estadísticas
  const totalTrades = trades.length;
  const winningTrades = trades.filter(t => t.result > 0).length;
  const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;
  const totalProfit = trades.reduce((sum, trade) => sum + trade.result, 0);
  const avgProfit = totalTrades > 0 ? totalProfit / totalTrades : 0;
  const avgR = trades.reduce((sum, trade) => sum + (trade.r_multiple || 0), 0) / totalTrades;

  // Calcular Zella Score
  const consistency = winRate;
  const profitFactor = trades.reduce((acc, trade) => trade.result > 0 ? acc + trade.result : acc, 0) / 
                      Math.abs(trades.reduce((acc, trade) => trade.result < 0 ? acc + trade.result : acc, 0)) || 1;
  const maxDrawdown = Math.min(...chartData.map(d => d.cumulative)) || 0;
  const recoveryFactor = Math.abs(maxDrawdown) > 0 ? totalProfit / Math.abs(maxDrawdown) : 1;
  const avgWinLoss = avgProfit;

  const zellaScore = (
    (consistency * 0.2) +
    (profitFactor * 20) +
    (recoveryFactor * 10) +
    (avgWinLoss * 5)
  ) / 100;

  // Datos para el radar chart
  const radarData = [
    {
      metric: 'Win %',
      value: Math.min(100, consistency),
      fullMark: 100
    },
    {
      metric: 'Profit factor',
      value: Math.min(100, profitFactor * 25),
      fullMark: 100
    },
    {
      metric: 'Avg win/loss',
      value: Math.min(100, avgWinLoss > 0 ? 75 : 25),
      fullMark: 100
    },
    {
      metric: 'Recovery factor',
      value: Math.min(100, recoveryFactor * 25),
      fullMark: 100
    },
    {
      metric: 'Max drawdown',
      value: Math.min(100, maxDrawdown > 0 ? 25 : 75),
      fullMark: 100
    },
    {
      metric: 'Consistency',
      value: Math.min(100, consistency),
      fullMark: 100
    }
  ];

  return (
    <div className="space-y-8">
      {/* Estadísticas Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Net P&L</p>
              <p className="text-2xl font-bold text-white">${totalProfit.toFixed(2)}</p>
            </div>
            <DollarSign className="h-8 w-8 text-primary" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Trade Win %</p>
              <p className="text-2xl font-bold text-white">{winRate.toFixed(2)}%</p>
            </div>
            {winRate >= 50 ? (
              <TrendingUp className="h-8 w-8 text-green-500" />
            ) : (
              <TrendingDown className="h-8 w-8 text-red-500" />
            )}
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Profit Factor</p>
              <p className="text-2xl font-bold text-white">{profitFactor.toFixed(2)}</p>
            </div>
            <BarChart2 className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Avg R Multiple</p>
              <p className="text-2xl font-bold text-white">{avgR.toFixed(2)}R</p>
            </div>
            <Target className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Zella Score */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Zella Score</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#2D3748" />
                <PolarAngleAxis 
                  dataKey="metric" 
                  tick={{ fill: '#718096' }}
                />
                <PolarRadiusAxis 
                  angle={30} 
                  domain={[0, 100]} 
                  tick={{ fill: '#718096' }}
                />
                <Radar
                  name="Performance"
                  dataKey="value"
                  stroke="var(--color-primary)"
                  fill="var(--color-primary)"
                  fillOpacity={0.3}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Zella Score</span>
              <span className="text-2xl font-bold text-white">{(zellaScore * 100).toFixed(2)}</span>
            </div>
            <div className="w-full bg-dark-overlay rounded-full h-2 mt-2">
              <div 
                className="bg-primary rounded-full h-2" 
                style={{ width: `${Math.min(100, zellaScore * 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Daily Net Cumulative P&L */}
        <div className="card lg:col-span-2">
          <h3 className="text-lg font-semibold text-white mb-4">Daily Net Cumulative P&L</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                <XAxis 
                  dataKey="date" 
                  stroke="#718096"
                  tick={{ fill: '#718096' }}
                />
                <YAxis 
                  stroke="#718096"
                  tick={{ fill: '#718096' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A202C',
                    border: '1px solid #2D3748',
                    borderRadius: '0.5rem'
                  }}
                  labelStyle={{ color: '#E2E8F0' }}
                  itemStyle={{ color: 'var(--color-primary)' }}
                />
                <Area
                  type="monotone"
                  dataKey="cumulative"
                  stroke="var(--color-primary)"
                  fillOpacity={1}
                  fill="url(#colorCumulative)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Net Daily P&L */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Net Daily P&L</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                <XAxis 
                  dataKey="date" 
                  stroke="#718096"
                  tick={{ fill: '#718096' }}
                />
                <YAxis 
                  stroke="#718096"
                  tick={{ fill: '#718096' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A202C',
                    border: '1px solid #2D3748',
                    borderRadius: '0.5rem'
                  }}
                  labelStyle={{ color: '#E2E8F0' }}
                  itemStyle={{ color: '#E2E8F0' }}
                />
                <Bar
                  dataKey="result"
                  fill="var(--color-primary)"
                  radius={[4, 4, 0, 0]}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.result >= 0 ? '#10B981' : '#EF4444'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* R-Multiple Distribution */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">R-Multiple Distribution</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                <XAxis
                  dataKey="date"
                  stroke="#718096"
                  tick={{ fill: '#718096' }}
                />
                <YAxis
                  stroke="#718096"
                  tick={{ fill: '#718096' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A202C',
                    border: '1px solid #2D3748',
                    borderRadius: '0.5rem'
                  }}
                  labelStyle={{ color: '#E2E8F0' }}
                  formatter={(value: any) => [`${value}R`, 'R-Multiple']}
                />
                <Bar
                  dataKey="r_multiple"
                  fill="var(--color-primary)"
                  radius={[4, 4, 0, 0]}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.r_multiple >= 0 ? '#10B981' : '#EF4444'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingCharts;